import { QueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError } from './api.ts';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => error instanceof ApiError && error.retryable && failureCount < 3,
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 10_000),
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Query key convention: `[resource]` for lists (filters appended as a trailing
 * object), `[resourceSingular, id]` for one record. Realtime invalidates by prefix,
 * so always start keys with these names.
 */
export const queryKeys = {
  me: ['me'] as const,
  systemHealth: ['system-health'] as const,
  conversations: (filters?: Record<string, unknown>) => (filters ? (['conversations', filters] as const) : (['conversations'] as const)),
  conversation: (id: string) => ['conversation', id] as const,
  messages: (conversationId: string) => ['messages', conversationId] as const,
  contacts: (filters?: Record<string, unknown>) => (filters ? (['contacts', filters] as const) : (['contacts'] as const)),
  contact: (id: string) => ['contact', id] as const,
  registrations: (filters?: Record<string, unknown>) => (filters ? (['registrations', filters] as const) : (['registrations'] as const)),
  payments: (filters?: Record<string, unknown>) => (filters ? (['payments', filters] as const) : (['payments'] as const)),
  campaigns: (filters?: Record<string, unknown>) => (filters ? (['campaigns', filters] as const) : (['campaigns'] as const)),
  campaign: (id: string) => ['campaign', id] as const,
  deliveries: (filters?: Record<string, unknown>) => (filters ? (['deliveries', filters] as const) : (['deliveries'] as const)),
  connections: ['connections'] as const,
  notifications: ['notifications'] as const,
} satisfies Record<string, QueryKey | ((...args: never[]) => QueryKey)>;
